import {
	ChallengeScheme,
	CoorporateScheme,
	ErrorScheme,
	Letter,
	LetterScheme,
	Track,
	UserScheme,
} from "client-v2";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import media from "styled-media-query";
import { Button, ColorV2, Text } from "@adoptaunabuelo/react-components";
import EditLetterComponent, {
	EditLetterProps,
} from "../../../views/letter/section/EditLetter";
import { useSelector } from "react-redux";
import { State } from "../../../redux";
import SectionContainer from "../../container/SectionContainer";
import ReactPixel from "react-facebook-pixel";

const LetterContainer = styled.div`
	display: flex;
	flex: 1;
	aspect-ratio: 1/1.414;
	height: 100%;
	box-shadow: 0px 3px 6px 0px rgba(0, 0, 0, 0.06);
	${media.lessThan("medium")`
		aspect-ratio: unset;
        margin: 0px;
		width: 100%;
    `}
`;
const EditContainer = styled.div`
	display: none;
	flex-direction: column;
	background-color: ${ColorV2.surface.background};
	${media.lessThan("medium")`
        display: flex;
    `}
`;

const PDFLetter = (
	props: PDFLetterProps &
		(
			| { grandpa: UserScheme; challenge?: undefined }
			| { grandpa?: undefined; challenge: ChallengeScheme }
		)
) => {
	const colors =
		props.corporate && props.corporate.color
			? {
					buttonBackground: props.corporate.color.main,
					buttonColor: props.corporate.color.third,
			  }
			: {
					buttonBackground: ColorV2.surface.primary,
					buttonColor: "white",
			  };
	const appLocation = useSelector((state: State) => state.location.location);
	const isMobile = useSelector((state: State) => state.screen.isMobile);
	const translation = useSelector(
		(state: State) => state.location.translation
	);
	const containerRef = useRef<HTMLDivElement>(null);
	const [html, setHtml] = useState("");
	const [scale, setScale] = useState(1);
	const [loading, setLoading] = useState(false);
	const [success, setSuccess] = useState(false);
	const [letter, setLetter] = useState<LetterScheme | undefined>(undefined);

	useEffect(() => {
		loadHTML(scale);
	}, [
		props.font,
		props.personalInfo,
		props.personalInfo,
		props.stamp,
		props.content,
	]);

	useEffect(() => {
		const updateScale = () => {
			if (containerRef.current) {
				const containerWidth = containerRef.current.offsetWidth; // Ancho del contenedor
				const contentWidth = 793.7; // Ancho del contenido del iframe en píxeles (210mm convertido a píxeles)
				const newScale = containerWidth / contentWidth;
				setScale(newScale);
				loadHTML(newScale);
			}
		};
		updateScale();
		window.addEventListener("resize", updateScale);
		return () => {
			window.removeEventListener("resize", updateScale);
		};
	}, []);

	const loadHTML = async (scale: number) => {
		const url =
			props.view === "letter"
				? `${process.env.PUBLIC_URL}/templates/letter/letter.html`
				: `${process.env.PUBLIC_URL}/templates/letter/birthday.html`;
		const response = await fetch(url);
		let html = await response.text();
		html = html.replace("{{content}}", props.content);
		html = html.replace(
			"{{name}}",
			`${props.user.name} ${props.user.surname}`
		);
		html = html.replace(
			"{{years}}",
			`${dayjs().diff(dayjs(props.user.birthday?.iso), "year")} años`
		);
		html = html.replace(
			`font-family: "Poppins";`,
			`font-family: "${props.font}";`
		);

		html = html.replace(
			/transform: scale\(([^)]+)\);/,
			`transform: scale(${scale});`
		);

		// if (props.corporate) {
		// html = html.replace(
		// 	"{{stamp_img}}",
		// 	`${process.env.PUBLIC_URL}/templates/letter/stamp_empty.png`,
		// );
		// html = html.replace(
		// 	"{{corporate_logo}}",
		// 	`<img src="${props.corporate.logo?.url}"/>`,
		// );
		// } else {
		html = html.replace(
			"{{stamp_img}}",
			`${process.env.PUBLIC_URL}/templates/letter/stamp_retro.png`
		);
		html = html.replace("{{corporate_logo}}", "");
		// }
		html = html.replace(
			"{{aua_logo}}",
			`${process.env.PUBLIC_URL}/templates/letter/stamp_aua.png`
		);
		if (props.stamp) {
			html = html.replace("stamp-aua hide", "stamp-aua show");
		} else {
			html = html.replace("stamp-aua show", "stamp-aua hide");
		}
		if (props.personalInfo) {
			html = html.replace("header hide", "header show");
		} else {
			html = html.replace("header show", "header hide");
		}
		if (props.image) {
			html = html.replace(
				"{{user-image}}",
				`<div class="aua__page-break"></div> <div class="letter-image"><img src="${props.image}" /></div>`
			);
		} else {
			html = html.replace("{{user-image}}", "");
		}
		setHtml(html);
	};

	const onNextClick = () => {
		setLoading(true);
		Letter.create({
			userId: props.user.objectId,
			corporateId: props.corporate?.objectId,
			grandpaId: props.grandpa?.objectId,
			challengeId: props.challenge?.objectId,
			content: props.content,
			country: appLocation?.country_code,
			language: appLocation?.language,
			image: props.image,
			customization: {
				stamp: props.stamp,
				font: props.font,
				personalInfo: props.personalInfo,
			},
		})
			.then((result) => {
				//Track on Facebook
				ReactPixel.trackCustom("CartaEnviadaOK", {
					value: 49.99,
					currency: appLocation ? appLocation.currency.symbol : "€",
					product_id: "A123",
					product_name: "Producto E",
					customer_type: "CartaOK",
				});

				//Track on mixpanel
				Track.event({
					event: "letter sent",
					user: props.user,
					params: {
						view: props.view,
					},
				});

				setLetter(result.data);
				localStorage.removeItem("letter");
				setLoading(false);
				setSuccess(true);
			})
			.catch((e: ErrorScheme) => {
				setLoading(false);
				props.onFinish({ error: e.message });
			});
	};

	return (
		<SectionContainer
			childrenProps={{ style: { margin: 0, height: "calc(100% - 8px)" } }}
		>
			{!isMobile && (
				<Text type="p" weight="medium" style={{ marginBottom: 16 }}>
					Así quedaría tu carta
				</Text>
			)}
			<LetterContainer ref={containerRef}>
				<iframe
					title="Letter Preview"
					style={{
						border: "none",
						width: "100%",
						height: "100%",
					}}
					srcDoc={html}
				/>
			</LetterContainer>
			<EditContainer>
				<Text
					type="p"
					style={{ margin: "22px 0px", textAlign: "center" }}
					weight="medium"
				>
					{translation.letter_edit_title_03}
				</Text>
				<EditLetterComponent style={{ margin: "0px" }} {...props} />
				<Button
					style={{
						marginTop: 24,
						width: "100%",
						backgroundColor: success
							? ColorV2.surface.green
							: colors.buttonBackground,
						color: success ? "white" : colors.buttonColor,
					}}
					loading={loading}
					success={success}
					onSuccess={() => {
						props.onFinish({ data: letter });
					}}
					onClick={onNextClick}
				>
					{translation.letter_edit_button_next}
				</Button>
			</EditContainer>
		</SectionContainer>
	);
};
export default PDFLetter;
export interface PDFLetterProps extends EditLetterProps {
	user: UserScheme;
	corporate?: CoorporateScheme;
	content: string;
	image?: string;
	font: string;
	personalInfo: boolean;
	stamp: boolean;
	view: "letter" | "birthday";
	onFinish: (result: { data?: LetterScheme; error?: string }) => void;
}
